/**
 * <MarkerRenamer> — focused admin tool for giving the 82 FT markers
 * (and 33 predator markers) human-readable names.
 *
 * The map seed data comes from palworld.th.gl as anonymous
 * "FTPoint1..82" identifiers. Renaming each to its Palworld wiki name
 * (e.g. "Plateau of Beginnings", "Small Settlement") means raid Discord
 * embeds and LiveMap popups say something like "Lv 87 Bellanoir spawned
 * at Plateau of Beginnings" instead of the cryptic "Fast Travel FTPoint5".
 *
 * UX: table with inline-editable name field. Save-on-blur. Toast on
 * success/failure. Filter by category + search.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Save, MapPin, Swords, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

const CATEGORIES = [
  { id: "fast_travel", label: "Fast Travel", icon: MapPin },
  { id: "predator_pal", label: "Predator", icon: Swords },
];

export default function MarkerRenamer() {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("fast_travel");
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({}); // {id: pendingName}
  const [saving, setSaving] = useState({}); // {id: bool}
  const [savedFlash, setSavedFlash] = useState({}); // {id: bool} green check briefly
  const toast = useToast();

  const fetchMarkers = useCallback(async () => {
    setLoading(true);
    try {
      // Load each category separately so we never pull the 133k-row
      // firehose. Two ~80-row calls keep the page snappy.
      const [ft, pred] = await Promise.all(
        CATEGORIES.map((c) =>
          api.get("/admin/map/markers", { params: { category: c.id } })
        )
      );
      const merged = [
        ...(ft?.data?.markers || []),
        ...(pred?.data?.markers || []),
      ];
      setMarkers(merged);
    } catch (e) {
      toast.error?.(apiError(e, "Failed to load markers"));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchMarkers(); }, [fetchMarkers]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return markers
      .filter((m) => m.category === filter)
      .filter((m) => !needle ||
        (m.name || "").toLowerCase().includes(needle) ||
        (m.id || "").toLowerCase().includes(needle))
      .sort((a, b) => {
        // Sort by thgl_id numeric suffix when present so FTPoint1..82
        // appears in natural order; fall back to alpha.
        const aid = (a.id || "").replace(/\D+/g, "");
        const bid = (b.id || "").replace(/\D+/g, "");
        if (aid && bid) return parseInt(aid, 10) - parseInt(bid, 10);
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [markers, filter, search]);

  const saveOne = async (m) => {
    const newName = (edits[m.id] ?? "").trim();
    if (!newName || newName === m.name) return;
    setSaving((s) => ({ ...s, [m.id]: true }));
    try {
      await api.put(`/admin/map/markers/${m.id}`, { name: newName });
      // Optimistic update in-state — no full refetch needed
      setMarkers((prev) => prev.map((x) => x.id === m.id ? { ...x, name: newName } : x));
      setEdits((e) => { const { [m.id]: _, ...rest } = e; return rest; });
      setSavedFlash((f) => ({ ...f, [m.id]: true }));
      setTimeout(() => {
        setSavedFlash((f) => { const { [m.id]: _, ...rest } = f; return rest; });
      }, 1200);
    } catch (e) {
      toast.error?.(apiError(e, "Save failed"));
    } finally {
      setSaving((s) => ({ ...s, [m.id]: false }));
    }
  };

  const renamedCount = filtered.filter((m) =>
    filter === "fast_travel"
      ? (m.name || "").toLowerCase() !== "fast travel"
      : !(m.name || "").startsWith("Predator ")
  ).length;

  return (
    <div className="space-y-4" data-testid="admin-marker-renamer">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <MapPin size={18} className="text-amber-400" /> Rename Map Markers
        </h2>
        <p className="text-xs text-[var(--muted)] mt-1 max-w-2xl">
          Replace generic IDs like <code className="font-mono">FTPoint21</code>{" "}
          with the actual Palworld place names from your in-game map. Renamed
          markers flow through to raid Discord embeds, LiveMap popups, and the
          raid system within ~60 seconds.
        </p>
      </header>

      {/* Filter + search row */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="renamer-controls">
        <div className="flex gap-1">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const count = markers.filter((m) => m.category === c.id).length;
            return (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className={
                  "px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 border transition-colors " +
                  (filter === c.id
                    ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                    : "border-white/10 hover:border-white/20")
                }
                data-testid={`renamer-filter-${c.id}`}
              >
                <Icon size={11} /> {c.label} ({count})
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            type="text"
            placeholder="Search by name or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-md pl-7 pr-2 py-1.5 text-xs focus:border-[var(--teal-soft)] outline-none"
            data-testid="renamer-search"
          />
        </div>
        <span className="text-xs text-[var(--muted)]" data-testid="renamer-progress">
          {renamedCount}/{filtered.length} renamed
        </span>
      </div>

      {/* Marker table */}
      {loading ? (
        <div className="text-sm text-[var(--muted)] glass-card p-4">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[var(--muted)] glass-card p-4">No markers match.</div>
      ) : (
        <ul className="space-y-1" data-testid="renamer-list">
          {filtered.map((m) => {
            const pending = edits[m.id];
            const dirty = pending !== undefined && pending.trim() !== m.name;
            const thglId = (() => {
              try {
                const meta = typeof m.metadata === "string"
                  ? JSON.parse(m.metadata || "{}")
                  : (m.metadata || {});
                return meta.thgl_id || meta.id || "";
              } catch { return ""; }
            })();
            return (
              <li
                key={m.id}
                className="flex items-center gap-2 px-3 py-2 bg-black/30 border border-white/5 rounded-md text-sm"
                data-testid={`renamer-row-${m.id}`}
              >
                <span className="text-[10px] font-mono text-[var(--muted)] w-20 truncate" title={m.id}>
                  {thglId || m.id}
                </span>
                <input
                  type="text"
                  value={pending !== undefined ? pending : m.name}
                  onChange={(e) => setEdits((x) => ({ ...x, [m.id]: e.target.value }))}
                  onBlur={() => dirty && saveOne(m)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.currentTarget.blur(); }
                    if (e.key === "Escape") {
                      setEdits((x) => { const { [m.id]: _, ...rest } = x; return rest; });
                    }
                  }}
                  className={
                    "flex-1 bg-transparent border border-transparent focus:border-amber-400/40 rounded px-2 py-1 text-sm outline-none transition-colors " +
                    (dirty ? "border-amber-500/30 bg-amber-500/5" : "")
                  }
                  placeholder={m.category === "fast_travel" ? "e.g. Plateau of Beginnings" : "e.g. Predator Anubis"}
                  data-testid={`renamer-input-${m.id}`}
                />
                <span className="text-[10px] text-[var(--muted)] font-mono w-24 text-right">
                  ({Math.round(m.x)}, {Math.round(m.y)})
                </span>
                {saving[m.id] ? (
                  <span className="w-5 h-5 grid place-items-center text-amber-400">…</span>
                ) : savedFlash[m.id] ? (
                  <Check size={14} className="text-emerald-400" />
                ) : dirty ? (
                  <button
                    type="button"
                    onClick={() => saveOne(m)}
                    className="text-amber-300 hover:text-amber-200 p-1 rounded"
                    data-testid={`renamer-save-${m.id}`}
                    title="Save"
                  >
                    <Save size={14} />
                  </button>
                ) : (
                  <span className="w-5" />
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[10px] text-[var(--muted)] mt-3">
        Tip: press <kbd className="font-mono bg-white/10 px-1 rounded">Enter</kbd> to save,{" "}
        <kbd className="font-mono bg-white/10 px-1 rounded">Esc</kbd> to discard. Renames apply
        immediately to the LiveMap and to the raid system within 60 s.
      </p>
    </div>
  );
}
